import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { HERO_SLIDES, BRAND } from "@/lib/data";

export default function Hero() {
    const [idx, setIdx] = useState(0);

    useEffect(() => {
        const t = setInterval(() => setIdx((i) => (i + 1) % HERO_SLIDES.length), 6000);
        return () => clearInterval(t);
    }, []);

    return (
        <section
            data-testid="hero-section"
            className="relative h-[78vh] min-h-[520px] md:h-[85vh] md:min-h-[640px] flex items-end overflow-hidden bg-ink"
        >
            {/* Sliding background images */}
            <AnimatePresence>
                <motion.div
                    key={idx}
                    initial={{ opacity: 0, scale: 1 }}
                    animate={{ opacity: 1, scale: 1.04 }}
                    exit={{ opacity: 0 }}
                    transition={{ opacity: { duration: 1.4 }, scale: { duration: 8, ease: "linear" } }}
                    className="absolute inset-0"
                >
                    <img
                        src={HERO_SLIDES[idx].url}
                        alt={HERO_SLIDES[idx].alt}
                        className="w-full h-full object-cover"
                        loading={idx === 0 ? "eager" : "lazy"}
                    />
                </motion.div>
            </AnimatePresence>

            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/70" />

            <div className="container-wide relative z-10 pb-16 md:pb-24 pt-16">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    className="max-w-3xl"
                >
                    <div className="flex items-center gap-4 mb-5">
                        <span className="divider-rule bg-bone" />
                        <span className="text-[11px] tracking-[0.3em] uppercase text-bone/85 font-medium">
                            Central Coast &amp; Newcastle · NSW
                        </span>
                    </div>
                    <h1
                        data-testid="hero-headline"
                        className="font-display text-bone text-3xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight font-light"
                    >
                        <span className="block">Custom homes &amp;</span>
                        <span className="block italic text-clay">renovations</span>
                        <span className="block text-bone/85 font-extralight">
                            on the Central Coast.
                        </span>
                    </h1>

                    <p className="mt-6 max-w-xl text-sm md:text-lg text-bone/75 leading-relaxed">
                        {BRAND.tagline}. Justin Brazier and his team handle every aspect of the build — from new builds and renovations to extensions and property development.
                    </p>

                    <div className="mt-8 flex flex-wrap gap-3 md:gap-4">
                        <Link to="/contact" data-testid="hero-cta-contact" className="btn-primary">
                            Get in Touch
                            <ArrowRight size={16} strokeWidth={1.8} />
                        </Link>
                        <Link to="/projects" data-testid="hero-cta-projects" className="btn-ghost-light">
                            See Projects
                        </Link>
                    </div>
                </motion.div>
            </div>

            {/* Slide indicators */}
            <div className="absolute bottom-6 md:bottom-10 right-6 md:right-12 z-10 flex items-center gap-3">
                {HERO_SLIDES.map((s, i) => (
                    <button
                        key={s.url}
                        onClick={() => setIdx(i)}
                        aria-label={`Go to slide ${i + 1}`}
                        data-testid={`hero-slide-dot-${i}`}
                        className={`h-px transition-all duration-500 ${
                            i === idx ? "w-10 bg-bone" : "w-5 bg-bone/40 hover:bg-bone/70"
                        }`}
                    />
                ))}
            </div>
        </section>
    );
}
